import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import type { Carrier } from '@klareospace/pharos-lib-types';

import type { CarrierEnablementFormInput } from '../schemas/carrier-enablement.schema';
import { buildCarrierEnablementSchema } from '../schemas/carrier-enablement.schema';
import { useEnableOnSite } from './use-enable-on-site';

export interface UseEnableCarrierFormReturn {
  form: UseFormReturn<CarrierEnablementFormInput>;
  isPending: boolean;
  onSubmit: (data: CarrierEnablementFormInput) => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useEnableCarrierForm(
  carrier: Carrier | null,
  siteId: string,
  spaceId: string,
  onSuccess: () => void,
): UseEnableCarrierFormReturn {
  const { t } = useTranslation('settings');
  const enableOnSite = useEnableOnSite();

  const schema = useMemo(
    () => buildCarrierEnablementSchema(t('sites.settings.carriers.validation.startBeforeEnd')),
    [t],
  );

  const form = useForm<CarrierEnablementFormInput>({
    defaultValues: { from: undefined, to: undefined },
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    form.reset({ from: undefined, to: undefined });
  }, [carrier, form]);

  function onSubmit(data: CarrierEnablementFormInput): void {
    if (!carrier) return;
    enableOnSite
      .mutateAsync({
        carrierId: carrier.id,
        from: data.from,
        siteId,
        spaceId,
        to: data.to,
      })
      .then(
        () => {
          form.reset();
          onSuccess();
        },
        () => {},
      );
  }

  return { form, isPending: enableOnSite.isPending, onSubmit };
}
